// @ts-nocheck
import { ROLES } from "app/constants";
import { useAppSelector } from "app/hooks";
import {
  GridModalFormProps,
  UseFormReturnType,
  Validator,
  enumToOptions,
  globalMessageHandler,
  useForm,
} from "common";
import { useParams } from "common/components/Form/ParamsContext";
import {
  BookingItem,
  BookingOptions,
  PrivateEventData,
  createPrivateEvent,
  getBookingService,
} from "features/bookings/bookingApi";
import {
  BOOKING_PAY_LOCATIONS,
  BOOKING_STATUSES,
} from "features/bookings/bookingConstants";
import useDictionaries from "features/dictionaries/useDictionaries";
import { EventItem } from "features/events/eventApi";
import useProgramSummary from "features/events/useProgramSummary";
import { useExchangeRate } from "features/exchange-rates/ExchangeRateProvider";
import { OptionItem } from "features/options/optionApi";
import { PartnerItem } from "features/partners/partnerApi";
import { selectProfile } from "features/profile/profileSlice";
import useAccess from "features/profile/useAccess";
import { omit } from "lodash";
import I18n from "i18n";
import { useEffect, useMemo, useState } from "react";
import { Params } from "react-router-dom";
import BookingOptionValidator from "../../features/bookings/BookingOptionValidator";
import { checkEventOnlineLimits } from "./GlobalOnlineCheckModal";
import { bookingValidationMessages } from "./bookingValidationErrors";

export interface UseBookingFormProps extends GridModalFormProps {
  isPartnerView?: boolean;
  createPrivateEventData?: PrivateEventData;
}

type RouteParams = {
  booking_id?: string;
};
type CapitalizeWords = { [key: string]: boolean };
export interface UseBookingFormReturnType extends UseFormReturnType<
  BookingItem,
  BookingOptions
> {
  optionValidator: BookingOptionValidator;
  params?: Readonly<
    Params<
      | "booking_id"
      | "event_id"
      | "boat_id"
      | "program_id"
      | "schedule_id"
      | "program_variant_id"
      | "partner_id"
      | "partner_group_id"
      | "booking_option_id"
    >
  >;
  cancelBooking: () => Promise<boolean>;
  activateBooking: () => Promise<boolean>;
  statusChanging: boolean;
  capitalizeWords: CapitalizeWords;
  setCapitalizeWords: (capitalizeWords: CapitalizeWords) => void;
  event?: EventItem | null;
  selectedPartnerGroup?: PartnerItem | null;
  priceMode: "normal" | "total";
  setPriceMode: (priceMode: "normal" | "total") => void;
  copyBookingDataToBilling: () => void;
}

export default function useBookingForm({
  id,
  afterSave,
  defaultValues,
  listItem,
  isPartnerView,
  createPrivateEventData,
}: UseBookingFormProps): UseBookingFormReturnType {
  const profile = useAppSelector(selectProfile);
  const params = useParams<
    | "booking_id"
    | "event_id"
    | "boat_id"
    | "program_id"
    | "schedule_id"
    | "program_variant_id"
    | "partner_id"
    | "partner_group_id"
    | "booking_option_id"
  >();

  const scope = {
    events: params.event_id,
    boats: params.boat_id,
    programs: params.program_id,
    schedules: params.schedule_id,
    "program-variants": params.program_variant_id,
    partners: params.partner_id,
    "partner-groups": params.partner_group_id,
  };
  const service = getBookingService(scope);
  const { dictionaries, resolveOptions, filterOptions, resolveOption } =
    useDictionaries();
  const { hasRoles } = useAccess();
  const { setCurrency } = useExchangeRate();

  const [priceMode, setPriceMode] = useState<"normal" | "total">(
    listItem?.event?.private || listItem?.planned_event?.private
      ? "total"
      : "normal",
  );
  const form = useForm<BookingItem, BookingOptions>({
    id: params.booking_id || id,
    ignoreNavigateAfterCreate: true,
    translationCategory: "Booking",
    rules: {
      program_variant_id: params.program_variant_id
        ? Validator.rule.optional()
        : Validator.rule.any().required(),
      status: Validator.rule.string().required(),
      //name: Validator.rule.string().required(),
      //phone: Validator.rule.string().required(),
      number_of_adults: Validator.rule.any().required(),
      //sku: Validator.rule.string().required(),
      adult_price: Validator.rule.any().required(),
      kid_price: Validator.rule.any().required(),
      currency: Validator.rule.any().required(),
    },
    //ignoredIsDirtyAttributes: ["customer_requests_invoice"],
    afterSave(newItem, isCreate) {
      if (newItem.event_id) {
        checkEventOnlineLimits(newItem.event_id);
      }
      // Partner nézetben create után ne zárjuk be a modalt,
      // hogy a partner megnyithassa a fizetési link UI-t
      if (isPartnerView && isCreate) {
        // Ne hívjuk meg az eredeti afterSave-t (ami bezárná a modalt),
        // a form automatikusan update módba vált (mert newItem.id létezik)
        afterSave && afterSave(newItem, isCreate);
        return;
      }
      afterSave && afterSave(newItem, isCreate);
    },
    /*afterChange(newItem, id, option) {
      if (id === "program_variant_id") {
        loadData();
      }
    },*/
    defaultValues: {
      ...defaultValues,
      ...listItem,
      paid: isPartnerView ? false : true,
      currency: isPartnerView ? undefined : "EUR",
      ...(createPrivateEventData ? { status: "active" } : {}),
    },
    transformChange: (item, id, option, oldItem) => {
      let newItem = Object.assign({}, item);
      if (["partner_name", "name"].includes(id) && capitalizeWords[id]) {
        newItem = { ...newItem, [id]: _capitalizeWords(item[id]) };
      }
      if (id === "number_of_adults" && priceMode === "total") {
        const oldCount = oldItem?.number_of_adults || 1;
        const count = item[id];
        let adult_price = (item.adult_price * oldCount) / count;
        if (!!adult_price && adult_price > 0 && count > 0) {
          newItem = { ...newItem, adult_price };
        } else {
          newItem = { ...newItem, adult_price: 0 };
        }
      }
      if (id === "number_of_kids" && priceMode === "total") {
        const oldCount = oldItem?.number_of_kids || 1;
        const count = item[id];
        let kid_price = (item.kid_price * oldCount) / count;
        if (!!kid_price && kid_price > 0 && count > 0) {
          newItem = { ...newItem, kid_price };
        } else {
          newItem = { ...newItem, kid_price: 0 };
        }
      }
      if (id === "email" && item[id]) {
        newItem[id] = item[id].trim();
      }
      if (id === "pay_location") {
        if (!isPartnerView) {
          newItem.paid = newItem[id] !== "dock";
        }
      }
      if (id === "customer_requests_invoice" && item[id] === true) {
        const currentBilling = item.billing_data || {};
        const needsPrefill =
          !currentBilling.first_name &&
          !currentBilling.last_name &&
          !currentBilling.phone &&
          !currentBilling.email;

        if (needsPrefill) {
          const nameParts = (item.name || "").trim().split(" ");
          const firstName = nameParts.length > 0 ? nameParts[0] : "";
          const lastName =
            nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

          newItem.billing_data = {
            first_name: firstName,
            last_name: lastName,
            phone: item.phone || "",
            email: item.email || "",
            country: currentBilling.country || "",
            postcode: currentBilling.postcode || "",
            city: currentBilling.city || "",
            street_address: currentBilling.street_address || "",
            company_name: currentBilling.company_name,
            vat_number: currentBilling.vat_number,
            apartment: currentBilling.apartment,
            county: currentBilling.county,
          };
        }
      }
      return newItem;
    },
    afterChange: (newItem, id, option) => {
      if (id === "program_variant_id") {
        const prices = getPrices(newItem[id]);
        const variant = resolveOption("variants", newItem[id]);
        form.setAttributes({ ...prices, sku: variant?.sku });
      }
      if (id === "currency" && newItem.currency) {
        setCurrency(newItem.currency as "EUR" | "HUF");
      }
    },
    onValidationFailed: (_item, error) => {
      globalMessageHandler.snack({
        severity: "error",
        message:
          bookingValidationMessages(error).join("\n") ||
          I18n.t("App.formHasErrors"),
      });
    },
    /*afterLoadOptions(newOptions) {
      const program_variant_id: any[] | undefined = (options as any)
        .program_variant_id;
      if (program_variant_id && program_variant_id.length === 1) {
        if (!form.item.program_variant_id) {
          form.setAttributes(
            {
              program_variant_id: program_variant_id[0].value,
            },
            true
          );
        }
      }
    },*/
    ...service,
    createItem: createPrivateEventData
      ? (booking: BookingItem) =>
          createPrivateEvent(createPrivateEventData, booking)
      : service.createItem,
    loadOptions: undefined,
    customValidation(newItem) {
      const optionErrors: any = optionValidator.validateOptions();

      let errors: any = {};
      if (optionErrors) {
        errors.booking_options = optionErrors;
      }
      if (newItem.customer_requests_invoice) {
        // Validate billing data
        const billingErrors: any = {};
        let billingRules = {
          first_name: Validator.rule.string().required(),
          last_name: Validator.rule.string().required(),
          country: Validator.rule.string().required(),
          postcode: Validator.rule.string().required(),
          city: Validator.rule.string().required(),
          street_address: Validator.rule.string().required(),
          phone: Validator.rule.string().required(),
          email: Validator.rule
            .string()
            .email({ tlds: { allow: false } })
            .required(),
        };
        const result = Validator.validate(
          billingRules,
          newItem.billing_data,
          "Booking",
        );
        if (!result.success) {
          errors.billing_data = result.errors;
        }
      }
      return errors;
    },
  });
  console.log({ isDirty: form.getIsDirty("customer_requests_invoice") });
  const event = getEvent();

  function getPrices(variant_id: number) {
    const v = event.variants.find(variant => variant.variant_id == variant_id);
    return {
      adult_price: parseFloat(v?.adult_price) || 0,
      kid_price: parseFloat(v?.kid_price) || 0,
    };
  }
  const {
    variants,
    options: _options,
    summary,
  } = useProgramSummary({ item: event });

  const selectedPartnerGroup = useMemo(() => {
    return resolveOption<PartnerItem>("partners", form.item?.partner_id);
  }, [form.item?.partner_id]);

  const { options } = useMemo(() => {
    return {
      options: {
        ...dictionaries,
        partner_id: dictionaries.partners.filter(p => p.active),
        recent_partner_id: dictionaries.recent_partners,
        sales_person_id: dictionaries.salespersons,
        program_variant_id: variants,
        booking_options: prepareOptions(),
        status: enumToOptions(BOOKING_STATUSES, "Booking.options.status"),
        pay_location: enumToOptions(
          BOOKING_PAY_LOCATIONS,
          isPartnerView
            ? "Booking.options.pay_location_partner"
            : "Booking.options.pay_location",
        ),
        currency: [
          { value: "EUR", label: "EUR" },
          { value: "HUF", label: "HUF" },
        ],
      } as any as Dictionary,
    };
  }, [variants, _options]);

  const [statusChanging, setStatusChanging] = useState(false);
  const [capitalizeWords, setCapitalizeWords] = useState<CapitalizeWords>({
    name: true,
    partner_name: true,
  });

  function getEvent(): EventItem | null | undefined {
    if (form.item?.planned) {
      if (form.item?.planned_event) {
        return form.item.planned_event;
      }
    }
    if (form.item?.event) {
      return form.item.event;
    }
    if (defaultValues?.event) {
      return defaultValues.event;
    }
    return null;
  }

  function prepareOptions() {
    let groups: any = {};
    _options?.forEach(o => {
      const d = resolveOption<OptionItem>("options", o.option_id);
      if (d.program_id === event.program_id) {
        const id = d?.option_group?.id || -1;
        if (!groups[id]) {
          groups[id] = {
            ...d?.option_group,
            items: [],
          };
        }
        groups[id].items.push({
          ...omit(d, "option_group"),
          price: o?.price ? parseFloat(o?.price) : 0,
          limit: o?.limit,
          number_of_bookings: o?.number_of_bookings,
          closed: o?.closed,
        });
      }
    });
    //console.log({ groups: Object.values(groups) });

    const options = Object.values(groups);
    // Sort options by sort_by
    options.sort((a, b) => {
      const sortByA = a?.sort_by ?? 0;
      const sortByB = b?.sort_by ?? 0;
      return sortByA - sortByB;
    });

    // Sort items inside each option by name
    options.forEach(option => {
      option.items?.sort((a, b) => {
        const nameA = a?.name?.toLowerCase() ?? "";
        const nameB = b?.name?.toLowerCase() ?? "";
        return nameA.localeCompare(nameB);
      });
    });
    return options;
  }

  const rules: any = {};
  if (form.item.custom_partner) {
    rules.partner_name = Validator.rule.any().required();
  } else {
    rules.partner_id = Validator.rule.any().required();
  }
  if (event?.program?.self_seating) {
    rules.email = Validator.rule
      .string()
      .required()
      .email({ tlds: { allow: false } });
  } else if (!!form.item.email) {
    rules.email = Validator.rule.string().email({ tlds: { allow: false } });
  }
  if (!form.item.planned && !createPrivateEventData) {
    rules.event_id = Validator.rule.any().required();
  }
  if (form.item.planned) {
    rules.planned_datetime = Validator.rule.any().required();
  }

  // pay_location required for partner users (unless monthly_billing) or if partner group requires it
  if (hasRoles(ROLES.PARTNER)) {
    const isMonthlyBillingPartner = !!profile?.partner?.monthly_billing;
    if (!isMonthlyBillingPartner) {
      rules.pay_location = Validator.rule.any().required();
    }
  } else if (selectedPartnerGroup?.partner_group?.pay_location_req) {
    // Non-partner users: check if the selected partner's group requires pay location
    console.log("pay_location required by partner group:", {
      selectedPartnerGroup,
      partner_group: selectedPartnerGroup?.partner_group,
      pay_location_req: selectedPartnerGroup?.partner_group?.pay_location_req,
    });
    rules.pay_location = Validator.rule.any().required();
  }

  if (hasRoles(ROLES.PARTNER)) {
    rules.email = Validator.rule
      .string()
      .required()
      .email({ tlds: { allow: false } });
    rules.phone = Validator.rule.any().required();
    rules.name = Validator.rule.any().required();
    rules.currency = Validator.rule.any().required();
  }
  form.addRules(rules);

  const optionValidator = new BookingOptionValidator({
    booking: form.item,
    //@ts-ignore
    options: options.booking_options,
  });

  async function setStatus(status: BOOKING_STATUSES) {
    setStatusChanging(true);
    const { success, data } = await service.updateItem(form.item.id, {
      status,
    });
    setStatusChanging(false);
    if (success) {
      form.setAttributes({ status }, true);
      afterSave && afterSave({ ...form.item, status }, false);
    }
    return success;
  }

  async function cancelBooking() {
    globalMessageHandler.confirm(
      {
        message: "Biztosan lemondod a foglalást",
        severity: "info",
      },
      async () => {
        const success = await setStatus(BOOKING_STATUSES.CANCELED);
        if (success) {
          globalMessageHandler.snack({
            message: "Sikeresen visszavontad a foglalást",
            severity: "success",
          });
        }
      },
    );
  }
  async function activateBooking() {
    const success = await setStatus(BOOKING_STATUSES.ACTIVE);
    if (success) {
      globalMessageHandler.snack({
        message: "Sikeresen visszavontad a foglalást",
        severity: "success",
      });
    }
    return !!success;
  }

  function _capitalizeWords(value: any) {
    if (!!value) {
      let words = value.split(" ");
      let capitalizedWords: any[] = [];
      words?.forEach((word: any) => {
        capitalizedWords.push(word.charAt(0).toUpperCase() + word.slice(1));
      });
      return capitalizedWords.join(" ").trim().replace(/\s+/g, " ");
    } else {
      return value;
    }
  }

  function copyBookingDataToBilling() {
    const currentBilling = form.item.billing_data || {};
    const nameParts = (form.item.name || "").trim().split(" ");
    const firstName = nameParts.length > 0 ? nameParts[0] : "";
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

    form.setAttributes({
      billing_data: {
        first_name: firstName,
        last_name: lastName,
        phone: form.item.phone || "",
        email: form.item.email || "",
        country: currentBilling.country || "",
        postcode: currentBilling.postcode || "",
        city: currentBilling.city || "",
        street_address: currentBilling.street_address || "",
        company_name: currentBilling.company_name,
        vat_number: currentBilling.vat_number,
        apartment: currentBilling.apartment,
        county: currentBilling.county,
      },
    });
  }

  function setDefaultProgramVariant() {
    if (options.program_variant_id && options.program_variant_id.length === 1) {
      if (!form.item.program_variant_id) {
        const program_variant_id = options.program_variant_id[0].value;
        form.setAttributes(
          {
            sku: options.program_variant_id[0].sku,
            program_variant_id,
            ...getPrices(program_variant_id),
          },
          true,
        );
      }
    }
  }

  useEffect(() => {
    setDefaultProgramVariant();
  }, [options]);

  useEffect(() => {
    const currentCurrency = form.item?.currency;
    if (currentCurrency === "EUR" || currentCurrency === "HUF") {
      setCurrency(currentCurrency);
    }
  }, [form.item?.currency, setCurrency]);

  return {
    ...form,
    options,
    params,
    optionValidator,
    activateBooking,
    cancelBooking,
    statusChanging,
    capitalizeWords,
    setCapitalizeWords,
    event,
    priceMode,
    setPriceMode,
    selectedPartnerGroup,
    copyBookingDataToBilling,
  };
}
